import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { SharedService } from 'src/app/services/shared.service';
import { TerminalFlagsService } from './terminal-flags.service';

/**
 * Whether this business may open the terminal feature at all.
 *
 * The same `show_terminal_menu` the side menu is filtered on, asked of the
 * route. Hiding a menu row removes the way in, not the address: the URL is
 * still live, and a bookmark, a shared link, the browser's history or a typed
 * path all walk straight past a hidden menu. The server refuses the
 * operations that matter, so nothing unsafe happens without this — but being
 * walked through ordering and pairing a feature that will not switch on is
 * its own kind of broken, and it is the business that discovers it.
 *
 * `canMatch` rather than `canActivate` so the answer comes before the lazy
 * chunk is fetched: a business without the feature never downloads it.
 *
 * Turned away goes to the business's own landing page — the first menu it
 * does have — because sending it to a dashboard it cannot see would be the
 * same bug one route along. `/app/dashboard` covers the window before the
 * menu has arrived.
 *
 * ## Why this file, and not `fm-terminal.guards.ts`
 *
 * That one reaches for `FmTerminalApi`, and whatever a guard on the lazy
 * route imports is pulled into the eager bundle with it. This guard's whole
 * job is to keep that chunk from loading, so it depends on nothing from
 * inside the feature but the flag — which the side menu already has loaded.
 */
export const canOpenTerminalGuard: CanMatchFn = () => {
  const flags = inject(TerminalFlagsService);
  const shared = inject(SharedService);
  const router = inject(Router);

  if (flags.showTerminalMenu()) { return true; }

  const landing = shared.sideMenus$.value?.[0]?.path || '/app/dashboard';
  return router.createUrlTree([landing]);
};
